import {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Button, message, Modal, Tooltip } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { DevicesAPI } from 'api/DevicesAPI';
import { PartnerHigoDevice } from 'models/higoDevice';
import React, { FC, ReactNode } from 'react';
import { useMutation } from 'react-query';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

interface DeleteDeviceTokenButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  device: PartnerHigoDevice;
  tooltip: ReactNode;
  onDelete?: (deviceId: number) => void;
}

export const DeleteDeviceTokenButton: FC<DeleteDeviceTokenButtonProps> = ({
  device,
  tooltip,
  onDelete,
  ...buttonProps
}) => {
  const [mutate, { isLoading }] = useMutation(DevicesAPI.deleteDeviceToken);
  const intlMessage = useFormatMessage();

  const showConfirm = () =>
    Modal.confirm({
      title: intlMessage(
        { id: 'devices.token.delete.confirmation' },
        { serialNumber: device.serialNumber }
      ),
      icon: <ExclamationCircleOutlined />,
      okText: intlMessage({ id: 'yes' }),
      cancelText: intlMessage({ id: 'cancel' }),
      okType: 'danger',
      onOk: () =>
        mutate(device.id, {
          onSuccess: () => {
            message.success(
              intlMessage({ id: 'devices.token.delete.success' })
            );
            onDelete && onDelete(device.id);
          },
        }),
    });

  const button = (
    <Button
      id="button-delete-device-token"
      loading={isLoading}
      onClick={showConfirm}
      icon={<CloseCircleOutlined />}
      {...buttonProps}
    />
  );

  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};
